import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { MatchingOperator } from '@openx/types/targeting/audience.ts';
import { convertToPercent } from '@openx/utils/lib/convertToPercent.ts';
import { Trans, useTranslation } from 'react-i18next';
import type { AudiencePricingResult } from '../../utils/types.ts';

export type FeeSummaryProps = {
  totalFees: AudiencePricingResult['computePricing']['totals'];
  operator: AudiencePricingResult['computePricing']['operator'];
};

export const FeeSummary = ({ totalFees, operator }: FeeSummaryProps) => {
  const { t } = useTranslation();
  const pricePom = convertToPercent(totalFees.price_pom ?? 0);

  const tooltipMessage =
    operator === MatchingOperator.INTERSECTION ? (
      <Trans i18nKey="feeSummaryTooltipIntersection">
        The amount below represents <b>the total</b> of all CPM fees listed above. PoM fees are unavailable when Segment
        Groups are linked using the AND operator.
      </Trans>
    ) : (
      <Trans i18nKey="feeSummaryTooltipUnion">
        The amounts below reflect <b>the average</b> of all PoM and CPM fees listed above. You can select your preferred
        fee type during deal creation.
      </Trans>
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        my: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography data-test="fee-summary-title" variant="h3">
          {t('Fee Summary')}
        </Typography>
        <Tooltip data-test="fee-summary-tooltip" title={tooltipMessage} placement="right">
          <IconButton size="small">
            <InfoRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {operator === MatchingOperator.UNION && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            data-test="percent-of-media-label"
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('Percent of Media:')}
          </Typography>
          <Typography data-test="percent-of-media-value" variant="body1Bold" component="span">
            {pricePom}%
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          data-test="fixed-cpm-label"
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('Fixed CPM:')}
        </Typography>
        <Typography data-test="fixed-cpm-value" variant="body1Bold" component="span">
          $
          {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
            totalFees.price_cpm
          )}
        </Typography>
      </Box>
    </Box>
  );
};
